import type { GridRenderCellParams } from '@mui/x-data-grid-pro';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CustomFieldValueEditor } from '@/components/common/custom-field-value/CustomFieldValueEditor';
import { ItemSelectionDialog } from '@/components/common/custom-field-value/ItemSelectionDialog';
import { LinkEditDialog } from '@/components/common/custom-field-value/LinkEditDialog';
import { MultilineEditDialog } from '@/components/common/custom-field-value/MultilineEditDialog';
import { MultiValueListDialog } from '@/components/common/custom-field-value/MultiValueListDialog';
import { NoteEditDialog } from '@/components/common/custom-field-value/NoteEditDialog';
import { UserSelectionDialog } from '@/components/common/custom-field-value/UserSelectionDialog';
import { validateFieldValue } from '@/components/common/custom-field-value/validation';
import { DatePicker } from '@/components/common/date-picker';
import type { CustomField } from '@/lib/api/types/customField';
import {
  isDialogEditedCustomFieldType,
  isMultiValueCustomFieldType,
} from '@/lib/api/types/customField';
import type {
  CustomFieldValuesEntry,
  ItemDateValue,
  ItemItemValue,
  ItemLinkValue,
  ItemMultiLineTextValue,
  ItemNoteValue,
  ItemUserValue,
} from '@/lib/api/types/items';
import type { ItemTypeMap } from '@/lib/api/types/itemType';
import { useCustomFieldValueMutation } from '@/lib/mutations/item-store/useCustomFieldValueMutation';
import { toISODateString } from '@/lib/utils/common/dateFormats';
import { customFieldV2ToV1 } from '@/lib/utils/itemConvert';
import { useVisibleItem, useVisibleItems } from '../context/VisibleItemsContext';
import type { ItemsTableRow } from '../types';
import { EditCell } from './EditCell';

type LinkInput = { url: string; description?: string };

function toLinkDto(link: LinkInput) {
  const dto: { value: string; description?: string } = { value: link.url };
  if (link.description !== undefined) dto.description = link.description;
  return dto;
}

function toV2FieldValue(fieldType: CustomField['type'], value: unknown): CustomFieldValuesEntry {
  if (value === null || value === undefined) return null;
  if (fieldType === 'LINK') {
    const link = value as LinkInput;
    return link.url == null ? null : toLinkDto(link);
  }
  if (fieldType === 'MULTI_LINK' && Array.isArray(value)) {
    return (value as LinkInput[]).map(toLinkDto);
  }
  if (Array.isArray(value)) {
    return value.map((v) => ({ value: v as string | number | boolean }));
  }
  return { value: value as string | number | boolean };
}

interface CustomFieldEditCellProps {
  params: GridRenderCellParams<ItemsTableRow>;
  customField: CustomField;
  itemTypesById: ItemTypeMap;
}

export const CustomFieldEditCell = ({
  params,
  customField,
  itemTypesById,
}: CustomFieldEditCellProps) => {
  const { t } = useTranslation();
  const { patchItem } = useVisibleItems();
  const itemId = Number(params.id);
  const previousRowRef = React.useRef(params.row);
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const { mutate, isPending, isSuccess, isError, error } = useCustomFieldValueMutation({
    itemId,
    options: {
      onError: () => {
        params.api.updateRows([previousRowRef.current]);
        patchItem(itemId, { customFieldValues: previousRowRef.current.customFieldValues });
      },
    },
  });

  const liveItem = useVisibleItem(itemId);
  const liveCustomFieldValues = liveItem?.customFieldValues ?? params.row.customFieldValues;
  const liveRow =
    liveCustomFieldValues !== params.row.customFieldValues
      ? { ...params.row, customFieldValues: liveCustomFieldValues }
      : params.row;

  const v1Value =
    liveCustomFieldValues?.[customField.id] !== undefined
      ? customFieldV2ToV1(liveCustomFieldValues[customField.id], customField.type)
      : undefined;

  const handleUpdate = (value: unknown) => {
    const isClear = value === null || value === undefined;
    if (!isClear) {
      const validation = validateFieldValue(t, customField.type, value);
      if (!validation.isValid) {
        setValidationError(validation.error ?? null);
        return;
      }
    }
    setValidationError(null);
    previousRowRef.current = { ...liveRow };
    const nextCustomFieldValues = {
      ...(liveCustomFieldValues ?? {}),
      [customField.id]: isClear ? null : toV2FieldValue(customField.type, value),
    };
    params.api.updateRows([{ id: itemId, customFieldValues: nextCustomFieldValues }]);
    patchItem(itemId, { customFieldValues: nextCustomFieldValues });
    mutate({
      fieldId: customField.id,
      fieldType: customField.type,
      value: isClear ? null : value,
    });
  };

  const liveValue = params.colDef.valueGetter
    ? params.colDef.valueGetter(
        undefined as never,
        liveRow,
        params.colDef as never,
        params.api as never,
      )
    : params.value;
  const stopEdit = () => params.api.stopCellEditMode?.({ id: params.id, field: params.field });
  const onDialogOpenChange = (open: boolean) => {
    if (!open) stopEdit();
  };
  const readCell = params.colDef.renderCell?.({ ...params, row: liveRow, value: liveValue });

  if (isDialogEditedCustomFieldType(customField.type)) {
    if (isMultiValueCustomFieldType(customField.type)) {
      return (
        <>
          {readCell}
          <MultiValueListDialog
            open={true}
            onOpenChange={onDialogOpenChange}
            title={customField.name}
          >
            <CustomFieldValueEditor
              value={v1Value}
              customField={customField}
              itemTypesById={itemTypesById}
              isEditable
              showLabel={false}
              onUpdate={handleUpdate}
              isPending={isPending}
              isSuccess={isSuccess}
              isError={isError || !!validationError}
              error={validationError ? new Error(validationError) : (error ?? null)}
            />
          </MultiValueListDialog>
        </>
      );
    }

    if (customField.type === 'LINK') {
      const linkValue = v1Value as ItemLinkValue | undefined;
      return (
        <>
          {readCell}
          <LinkEditDialog
            open={true}
            onOpenChange={onDialogOpenChange}
            initialValue={
              linkValue?.LINK
                ? { url: linkValue.LINK, description: linkValue.description }
                : undefined
            }
            onSave={(link) => handleUpdate(link)}
            onClear={() => handleUpdate(undefined)}
            mode={linkValue?.LINK ? 'edit' : 'add'}
          />
        </>
      );
    }

    if (customField.type === 'USER') {
      const userValue = v1Value as ItemUserValue | undefined;
      return (
        <>
          {readCell}
          <UserSelectionDialog
            open={true}
            onOpenChange={onDialogOpenChange}
            selectedUserId={userValue?.USER}
            onSave={(userId) => handleUpdate(userId)}
            onClear={() => handleUpdate(undefined)}
            title={t(($) => $.adaptivePlanner.itemPanel.userField.selectUser)}
          />
        </>
      );
    }

    if (customField.type === 'ITEM') {
      const itemValue = v1Value as ItemItemValue | undefined;
      return (
        <>
          {readCell}
          <ItemSelectionDialog
            open={true}
            onOpenChange={onDialogOpenChange}
            itemTypesById={itemTypesById}
            selectedItemId={itemValue?.ITEM}
            excludedItemIds={itemValue?.ITEM !== undefined ? [itemValue.ITEM] : undefined}
            onSave={(itemId) => handleUpdate(itemId)}
            onClear={() => handleUpdate(undefined)}
            title={customField.name}
          />
        </>
      );
    }

    if (customField.type === 'MULTILINE_TEXT') {
      const text = (v1Value as ItemMultiLineTextValue | undefined)?.MULTILINE_TEXT;
      return (
        <>
          {readCell}
          <MultilineEditDialog
            open
            onOpenChange={onDialogOpenChange}
            initialValue={text}
            title={customField.name}
            onSave={(v) => handleUpdate(v)}
          />
        </>
      );
    }

    if (customField.type === 'NOTE') {
      const note = (v1Value as ItemNoteValue | undefined)?.NOTE;
      return (
        <>
          {readCell}
          <NoteEditDialog
            open
            onOpenChange={onDialogOpenChange}
            initialValue={note}
            onSave={(v) => handleUpdate(v)}
          />
        </>
      );
    }
  }

  if (customField.type === 'DATE') {
    const dateValue = (v1Value as ItemDateValue | undefined)?.DATE ?? null;
    return (
      <EditCell
        width={params.colDef.computedWidth}
        contentClassName="w-auto p-0"
        matchCellHeight={false}
        closeOnScrollOutOfView={{ api: params.api, id: params.id, field: params.field }}
      >
        <DatePicker
          value={dateValue}
          showClearButton
          onSelect={(next) => {
            handleUpdate(toISODateString(next));
            params.api.stopCellEditMode?.({
              id: params.id,
              field: params.field,
              ignoreModifications: true,
            });
          }}
        />
      </EditCell>
    );
  }

  return (
    <EditCell
      width={params.colDef.computedWidth}
      closeOnScrollOutOfView={{ api: params.api, id: params.id, field: params.field }}
    >
      <CustomFieldValueEditor
        value={v1Value}
        customField={customField}
        itemTypesById={itemTypesById}
        isEditable
        showLabel={false}
        autoFocus
        onUpdate={handleUpdate}
        isPending={isPending}
        isSuccess={isSuccess}
        isError={isError || !!validationError}
        error={validationError ? new Error(validationError) : (error ?? null)}
      />
    </EditCell>
  );
};
